// Kept in sync with the other React client; no platform or network dependencies.
export type PromptReservation = {
  shouldPrompt: boolean; retryAfterMs: number;
  reservationToken: string | null; reservationExpiresInMs: number | null;
};
type Acknowledgement = { ok: boolean; retryAfterMs: number };
type Options<T> = {
  reserve: () => Promise<T>; acknowledge: (_token: string) => Promise<Acknowledgement>;
  present: (_value: T) => void; hide: () => void;
};
const validDelay = (value: number) => Number.isFinite(value) && value >= 0 && value <= 172800000;

/** Server-relative deadlines avoid device timezone/clock differences. */
export class WorkAvailabilityPromptLifecycle<T extends PromptReservation> {
  private active = false;
  private disposed = false;
  private running = false;
  private failures = 0;
  private due = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private pending: { value: T; expires: number; shown: boolean; confirmed: boolean; closed: boolean } | null = null;
  private options: Options<T>;
  constructor(options: Options<T>) { this.options = options; }

  setActive(active: boolean) {
    if (this.disposed || this.active === active) return;
    this.active = active;
    this.clearTimer();
    if (!active) {
      if (this.pending && !this.pending.shown) {
        this.due = this.pending.expires;
        this.pending = null;
        this.options.hide();
      }
      return;
    }
    this.schedule(Math.max(700, this.due - Date.now()));
  }

  invalidate() {
    if (this.pending) return;
    this.due = 0;
    this.schedule(700);
  }

  presented() {
    if (!this.active || !this.pending || this.pending.shown || this.pending.confirmed) return;
    this.pending.shown = true;
    this.schedule(0);
  }

  close() {
    this.options.hide();
    if (this.pending) {
      this.pending.closed = true;
      if (this.active) this.pending.shown = true;
      if (this.pending.confirmed) this.pending = null;
      else this.schedule(0);
    }
  }

  dispose() {
    this.disposed = true;
    this.active = false;
    this.clearTimer();
  }

  private clearTimer() {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private schedule(delay: number) {
    this.clearTimer();
    if (this.active && !this.disposed) this.timer = setTimeout(() => { void this.check(); }, Math.max(0, delay));
  }

  private async check() {
    if (!this.active || this.disposed || this.running) return;
    this.running = true;
    let nextDelay = 0;
    const started = Date.now();
    try {
      if (this.pending?.confirmed && this.due <= started) {
        this.pending = null;
        this.options.hide();
      }
      if (this.pending && !this.pending.confirmed) {
        const pending = this.pending;
        if (pending.expires <= started) {
          this.pending = null;
          this.options.hide();
          this.due = started + 15000;
        } else if (!pending.shown) {
          this.due = pending.expires;
        } else {
          const result = await this.options.acknowledge(pending.value.reservationToken!);
          if (!result?.ok || !validDelay(result.retryAfterMs)) throw new Error('Invalid prompt acknowledgement');
          if (this.disposed) return;
          this.failures = 0;
          pending.confirmed = true;
          this.due = started + Math.max(15000, result.retryAfterMs);
          if (pending.closed) this.pending = null;
        }
      } else if (!this.pending && this.due <= started) {
        const result = await this.options.reserve();
        if (this.disposed) return;
        if (!result || typeof result.shouldPrompt !== 'boolean' || !validDelay(result.retryAfterMs)) {
          throw new Error('Invalid prompt decision');
        }
        this.due = started + Math.max(15000, result.retryAfterMs);
        if (result.shouldPrompt) {
          if (!result.reservationToken || !validDelay(result.reservationExpiresInMs!)
            || result.reservationExpiresInMs! > 120000) throw new Error('Invalid prompt reservation');
          const expires = started + result.reservationExpiresInMs!;
          if (this.active && expires > Date.now()) {
            this.pending = { value: result, expires, shown: false, confirmed: false, closed: false };
            this.options.present(result);
            this.due = expires;
          } else {
            this.due = Math.max(Date.now() + 15000, expires);
          }
        }
        this.failures = 0;
      }
      nextDelay = Math.max(0, this.due - Date.now());
      if (this.pending?.shown && !this.pending.confirmed) nextDelay = 0;
    } catch (error) {
      const status = (error as { status?: number; statusCode?: number })?.status
        ?? (error as { statusCode?: number })?.statusCode;
      if (status === 409) {
        this.pending = null;
        this.options.hide();
      }
      this.failures += 1;
      // Five failures open a five-minute circuit. Backgrounding never resets it.
      nextDelay = this.failures >= 5 ? 300000 : Math.min(120000, 15000 * 2 ** (this.failures - 1)) * (1 + Math.random() * .2);
      this.due = Date.now() + nextDelay;
    } finally {
      this.running = false;
      if (!this.disposed) this.schedule(nextDelay);
    }
  }
}
